/**
 * The graph/ abstraction layer (§3.4).
 *
 * RULE: no component or feature calls Microsoft Graph directly. Everything routes
 * through here, so versioning, error normalisation, throttling, pagination, and
 * $batch are centralised — and a Graph change is a contained fix.
 */
import {
  Client,
  type AuthenticationProvider,
} from '@microsoft/microsoft-graph-client';
import { acquireToken } from '@/auth/tokens';
import { normalizeGraphError } from './errors';
import { logQuery } from './queryLog';
import type {
  GraphCollection,
  GraphRequestOptions,
} from './types';

/** One Graph Client per scope-set (scopes drive the delegated token acquired). */
const clientCache = new Map<string, Client>();

function clientForScopes(scopes: readonly string[]): Client {
  const key = [...scopes].sort().join(' ') || '__no_scope__';
  let client = clientCache.get(key);
  if (!client) {
    const authProvider: AuthenticationProvider = {
      getAccessToken: () => acquireToken(scopes),
    };
    client = Client.initWithMiddleware({
      authProvider,
      defaultVersion: 'v1.0',
      // initWithMiddleware installs RetryHandler → honours Retry-After (§6).
    });
    clientCache.set(key, client);
  }
  return client;
}

function buildRequest(path: string, opts: GraphRequestOptions) {
  let req = clientForScopes(opts.scopes)
    .api(path)
    .version(opts.version ?? 'v1.0');
  if (opts.query) {
    for (const [k, v] of Object.entries(opts.query)) req = req.query({ [k]: v });
  }
  return req;
}

/** GET a single resource. */
export async function graphGet<T>(
  path: string,
  opts: GraphRequestOptions,
): Promise<T> {
  const started = performance.now();
  try {
    const res = (await buildRequest(path, opts).get()) as T;
    logQuery(path, 'GET', 200, performance.now() - started);
    return res;
  } catch (err) {
    logQuery(path, 'GET', 'error', performance.now() - started);
    throw normalizeGraphError(err);
  }
}

/**
 * GET a collection, following @odata.nextLink to completion (§6 progressive scan
 * callers can instead use graphGetPages for streaming).
 */
export async function graphGetAll<T>(
  path: string,
  opts: GraphRequestOptions,
): Promise<T[]> {
  const out: T[] = [];
  for await (const page of graphGetPages<T>(path, opts)) out.push(...page);
  return out;
}

/**
 * Async-generator pagination. Lets C4's progressive scan stream results with
 * visible progress instead of blocking on the full set (§6).
 */
export async function* graphGetPages<T>(
  path: string,
  opts: GraphRequestOptions,
): AsyncGenerator<T[], void, unknown> {
  const client = clientForScopes(opts.scopes);
  try {
    let started = performance.now();
    let res = (await buildRequest(path, opts).get()) as GraphCollection<T>;
    logQuery(path, 'GET', 200, performance.now() - started);
    yield res.value ?? [];
    while (res['@odata.nextLink']) {
      started = performance.now();
      res = (await client
        .api(res['@odata.nextLink'])
        .version(opts.version ?? 'v1.0')
        .get()) as GraphCollection<T>;
      logQuery(path + ' (page)', 'GET', 200, performance.now() - started);
      yield res.value ?? [];
    }
  } catch (err) {
    logQuery(path, 'GET', 'error', 0);
    throw normalizeGraphError(err);
  }
}

export interface BatchStep {
  id: string;
  method: 'GET';
  url: string; // relative Graph URL, e.g. '/organization'
}

export interface BatchResult<T = unknown> {
  id: string;
  status: number;
  body: T;
}

/**
 * $batch for parallel reads (§6). All steps share one scope-set/token.
 * Non-2xx steps are returned (not thrown) so callers can degrade gracefully.
 */
export async function graphBatch<T = unknown>(
  steps: readonly BatchStep[],
  opts: Pick<GraphRequestOptions, 'scopes' | 'version'>,
): Promise<BatchResult<T>[]> {
  const client = clientForScopes(opts.scopes);
  const started = performance.now();
  try {
    const payload = { requests: steps.map(({ id, method, url }) => ({ id, method, url })) };
    const res = (await client
      .api('/$batch')
      .version(opts.version ?? 'v1.0')
      .post(payload)) as { responses: Array<{ id: string; status: number; body: T }> };
    logQuery(`/$batch (${steps.length})`, 'POST', 200, performance.now() - started);
    return res.responses.map((r) => ({ id: r.id, status: r.status, body: r.body }));
  } catch (err) {
    logQuery(`/$batch (${steps.length})`, 'POST', 'error', performance.now() - started);
    throw normalizeGraphError(err);
  }
}

/** Test seam / advanced callers only. */
export function __resetGraphClientCache(): void {
  clientCache.clear();
}
